import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CreditCard,
  FileText,
  Shield,
  Cpu,
  Handshake,
  Store,
  Ticket as TicketIcon,
  Plus,
  X,
  ExternalLink,
  Link2,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import LinkPickerField from "components/Tickets/LinkPickerField";
import Skeleton from "components/Skeleton";

/**
 * RecordLinksPanel (SOUND-649 Phase 3) — the ticket workspace's link panel,
 * for every record.
 *
 * Until now only a TICKET could hold links; every other page could display what
 * someone had linked from a ticket, but not create one. So a MID could not point
 * at the DBA it trades under, because there was no surface to say so.
 *
 * This talks to /entity-links, which is symmetric: it asks "what is attached to
 * this record" and gets everything back regardless of which end of the edge the
 * record sits on. The component never learns the stored direction — that is the
 * point of the repo's `orient`, and it is why one panel works on both ends of
 * the same relationship.
 */

const ENTITY_META = {
  company: { label: "Company", icon: Building2, linkTo: (id) => `/companies/${id}` },
  mid: { label: "MID", icon: CreditCard, linkTo: (id) => `/mids/${id}` },
  application: { label: "Application", icon: FileText, linkTo: (id) => `/mid-applications/${id}` },
  dba: { label: "DBA", icon: Store, linkTo: null },
  ticket: { label: "Ticket", icon: TicketIcon, linkTo: (id) => `/tickets/${id}` },
  disputifierEnrollment: { label: "Disputifier", icon: Shield, linkTo: (id) => `/mids/${id}` },
  processor: { label: "Processor", icon: Cpu, linkTo: (id) => `/settings/processors/${id}` },
  partner: { label: "Partner", icon: Handshake, linkTo: (id) => `/companies/${id}` },
};

/** What a given record type can usefully be linked TO. */
const ADDABLE_FOR = {
  mid: ["dba", "application", "processor", "company"],
  application: ["dba", "mid", "processor"],
  company: ["mid", "application", "processor", "partner"],
  dba: ["mid", "application"],
};

export default function RecordLinksPanel({
  entityType,
  entityId,
  companyId,
  title = "Linked",
  // Rows that are NOT links — relationships derived from a foreign key, like
  // the company a MID belongs to. They render in the same panel because to a
  // reader they are the same question ("what is this connected to"), but they
  // carry no remove control: you cannot unlink ownership, only reassign it.
  //
  // Keeping them in one panel is also why the architecture distinction stays
  // legible. Ownership is pinned; associations can be added and removed.
  pinned = [],
  // Tickets are deliberately NOT shown here. They have their own section on
  // every record page which renders what a ticket actually needs — SC number,
  // title, status pill — and a generic link chip cannot match that. Showing
  // both meant every ticket appeared twice, once properly and once as a raw
  // UUID, since the ticket end of a migrated link has no cached label.
  //
  // The link still EXISTS and is still the same edge; this only decides what
  // this panel draws.
  exclude = ["ticket"],
  className = "",
}) {
  const auth = useAuth();
  const internal = isInternal(auth?.user);
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addType, setAddType] = useState(null);
  const [busy, setBusy] = useState(false);

  const addable = useMemo(() => ADDABLE_FOR[entityType] || [], [entityType]);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        `${apiRoutes.entityLinks}?type=${entityType}&id=${entityId}`,
        auth.user,
        effectiveCompanyId
      );
      const all = res.data?.data || [];
      setLinks(exclude.length ? all.filter((l) => !exclude.includes(l.otherType)) : all);
    } catch {
      // A record that cannot load its links should still render — the panel
      // says "none" rather than taking the page down.
      setLinks([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, effectiveCompanyId, exclude.join(",")]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async ({ entityId: toId, label }) => {
    setBusy(true);
    try {
      await makeApiRequest("post", apiRoutes.entityLinks, auth.user, effectiveCompanyId, {
        fromType: entityType,
        fromId: entityId,
        toType: addType,
        toId,
        label,
        role: "related",
      });
      toast({ status: "success", title: "Linked" });
      setAdding(false);
      setAddType(null);
      load();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to link",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (link) => {
    setBusy(true);
    try {
      await makeApiRequest(
        "delete",
        `${apiRoutes.entityLinks}/${link.linkId}`,
        auth.user,
        effectiveCompanyId
      );
      toast({ status: "success", title: "Unlinked" });
      load();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to unlink",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`surface p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Link2 className="h-3.5 w-3.5" />
          {title}
          {!loading && pinned.length + links.length > 0 && (
            <span className="text-muted-foreground/70">· {pinned.length + links.length}</span>
          )}
        </h3>
        {internal && addable.length > 0 && !adding && (
          <button
            type="button"
            className="btn btn-ghost h-7 px-2 text-xs"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Link
          </button>
        )}
      </div>

      {pinned.length > 0 && (
        <ul className="mb-1.5 space-y-1.5">
          {pinned.map((row) => {
            const Icon = row.icon || Link2;
            const body = (
              <>
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{row.label}</div>
                  {row.sublabel && (
                    <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
                      {row.sublabel}
                    </div>
                  )}
                </div>
              </>
            );
            return (
              <li
                key={row.key || row.label}
                className="group flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
              >
                {body}
                {row.href && (
                  <Link
                    to={row.href}
                    className="text-muted-foreground hover:text-brand"
                    title={`Open ${row.sublabel || "record"}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {loading ? (
        <Skeleton.Rect height={18} width={160} />
      ) : links.length === 0 && !adding ? (
        pinned.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing linked yet.</p>
        ) : null
      ) : (
        <ul className="space-y-1.5">
          {links.map((l) => {
            const meta = ENTITY_META[l.otherType] || {};
            const Icon = meta.icon || Link2;
            // A UUID is not a name. Migrated links carry no label on some ends,
            // and any future type could arrive unlabelled — say so rather than
            // printing an identifier a human cannot use.
            const label = l.otherLabel || "(unnamed)";
            const href = meta.linkTo ? meta.linkTo(l.otherId) : null;
            return (
              <li
                key={l.linkId}
                className="group flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{label}</div>
                  <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
                    {meta.label || l.otherType}
                  </div>
                </div>
                {/* A DBA has no detail page yet, so it simply has no open
                    control — better than a dead link. */}
                {href && (
                  <Link
                    to={href}
                    className="text-muted-foreground hover:text-brand"
                    title={`Open ${meta.label || l.otherType}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
                {internal && (
                  <button
                    type="button"
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    disabled={busy}
                    onClick={() => remove(l)}
                    title="Remove link"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap gap-1">
            {addable.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setAddType(t)}
                className={`badge ${addType === t ? "badge-brand" : "badge-muted"}`}
              >
                {ENTITY_META[t]?.label || t}
              </button>
            ))}
          </div>
          {addType && (
            <LinkPickerField
              entityType={addType}
              companyId={companyId}
              onSelect={add}
              placeholder={`Search ${ENTITY_META[addType]?.label || addType}…`}
              autoFocus
            />
          )}
          <button
            type="button"
            className="btn btn-ghost h-7 w-full text-xs"
            onClick={() => {
              setAdding(false);
              setAddType(null);
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
